/**
 * Pict-Provider-UserAPI
 * ---------------------
 * Browser-side client for the caller's own account (the self-service
 * /1.0/Account/* endpoints). Backs the Profile / Sign-in sections of the
 * settings hub. AppData.Account.Profile holds the last-loaded profile.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'UserAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderUserAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Account)
		{
			this.fable.AppData.Account = { Profile: null, LoadError: null };
		}
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	getMe()
	{
		this.initializeAppData();
		return libFrameworkClient.getJSON(this.fable, this._url('Account/Me'))
			.then((pBody) => { this.fable.AppData.Account.Profile = pBody.Account || null; return pBody.Account; })
			.catch((pErr) => { this.fable.AppData.Account.LoadError = pErr.message; return null; });
	}

	updateMe(pPatch)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Account/Me'), pPatch || {})
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not save.'); }
				if (pBody.Account) { this.fable.AppData.Account.Profile = pBody.Account; }
				return pBody.Account;
			});
	}

	changeEmail(pEmail)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Account/Email'), { Email: pEmail })
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not change email.'); }
				if (this.fable.AppData.Account.Profile) { this.fable.AppData.Account.Profile.Email = pBody.Email; }
				return pBody.Email;
			});
	}

	changePassword(pCurrent, pNew)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Account/Password'), { CurrentPassword: pCurrent, NewPassword: pNew })
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not change password.'); }
				return true;
			});
	}

	// --- Two-step verification (2FA) enrollment ------------------------------
	_post(pPath, pBody)
	{
		return libFrameworkClient.postJSON(this.fable, this._url(pPath), pBody || {})
			.then((pJSON) =>
			{
				if (!pJSON.Success) { throw new Error((pJSON && pJSON.Error) || 'Something went wrong.'); }
				return pJSON;
			});
	}

	getTwoFactor()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Account/TwoFactor'))
			.then((pBody) => pBody.TwoFactor || { Available: false })
			.catch(() => ({ Available: false }));
	}

	setPhone(pPhone) { return this._post('Account/Phone', { Phone: pPhone }); }
	verifyPhone(pToken, pCode) { return this._post('Account/Phone/Verify', { ChallengeToken: pToken, Code: pCode }); }
	enableTwoFactor(pChannel) { return this._post('Account/TwoFactor/Enable', { Channel: pChannel }); }
	confirmTwoFactor(pToken, pCode) { return this._post('Account/TwoFactor/Confirm', { ChallengeToken: pToken, Code: pCode }); }
	disableTwoFactor(pPassword) { return this._post('Account/TwoFactor/Disable', { Password: pPassword }); }
	generateRecoveryCodes() { return this._post('Account/TwoFactor/RecoveryCodes', {}); }

	getNotificationPrefs()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Account/NotificationPrefs'))
			.then((pBody) => ({ Prefs: (pBody && Array.isArray(pBody.Prefs)) ? pBody.Prefs : [], CanSMS: !!(pBody && pBody.CanSMS) }))
			.catch(() => ({ Prefs: [], CanSMS: false }));
	}

	// pPatch carries whichever channel(s) changed: { EmailEnabled? , SMSEnabled? }.
	setNotificationPref(pEventKey, pPatch)
	{
		let tmpBody = Object.assign({ EventKey: pEventKey }, pPatch || {});
		return libFrameworkClient.putJSON(this.fable, this._url('Account/NotificationPrefs'), tmpBody)
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not save.'); }
				return pBody;
			});
	}

	// ── OAuth connections: which plan sheets a token the person created may act in ──────────────
	// The authorized set could only ever grow (creating a plan sheet over a connector widens the calling
	// token), and nothing showed a person what a token had accumulated. These back the Connections screen.
	getOAuthConnections()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('OAuth/Connections'))
			.then((pBody) => (
			{
				Connections: (pBody && Array.isArray(pBody.Connections)) ? pBody.Connections : [],
				AvailablePlansheets: (pBody && Array.isArray(pBody.AvailablePlansheets)) ? pBody.AvailablePlansheets : []
			}))
			.catch(() => ({ Connections: [], AvailablePlansheets: [] }));
	}

	// pIDCustomers is the WHOLE set the connection should be authorized for; the server keeps the grant's
	// own plan sheet in it regardless, and refuses any plan sheet the caller is not a member of.
	setOAuthConnectionPlansheets(pIDOAuthClient, pIDCustomers)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('OAuth/Connection/' + pIDOAuthClient + '/Plansheets'),
			{ Plansheets: Array.isArray(pIDCustomers) ? pIDCustomers : [] })
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not update that connection.'); }
				return pBody;
			});
	}

	revokeOAuthConnection(pIDOAuthClient)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('OAuth/Connection/' + pIDOAuthClient + '/Revoke'), {})
			.then((pBody) =>
			{
				if (!pBody.Success) { throw new Error((pBody && pBody.Error) || 'Could not revoke that connection.'); }
				return pBody;
			});
	}

	// One guide from the self-describing API (orator-api-description): { Key, Title, Body }, Body being markdown.
	// The human half of the connect-Claude story reads its walkthrough from the same content the API publishes.
	getApiGuide(pKey)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('API/Guides/' + encodeURIComponent(pKey)))
			.then((pBody) => (pBody && pBody.Key) ? pBody : null)
			.catch(() => null);
	}
}

module.exports = PictProviderUserAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
